import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/OS/components/ui/card";
import { Button } from "@/OS/components/ui/button";
import { Input } from "@/OS/components/ui/input";
import { Switch } from "@/OS/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/OS/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/OS/components/ui/alert-dialog";
import { useToast } from "@/OS/components/ui/use-toast";
import {
  Bot,
  Check,
  Copy,
  MapPin,
  Pin,
  PinOff,
  ShieldAlert,
  Sparkles,
  X,
} from "lucide-react";

import { supabase } from "@/OS/integrations/supabase/client";
import { useSupabaseStore } from "@/store/SupabaseStore";
import { useAppStore } from "@/store";
import { sendMessageToModel, type AIConfig } from "@/services/ModelService";
import { AIModel, Message } from "@/types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cleanMarkdownBold } from "@/lib/utils";
import { globalNavigator } from "./WindowContent";
import { ensureDir } from "@/OS/utils/filesystem";
import { isOSAIBlocked, setOSAIBlocked } from "@/lib/osIntegration";
import { getAssetPath } from "@/OS/utils/assetPath";
import { MapleProgram, PROGRAMS } from "@/OS/utils/programs";
import { runAssistantAppCommand } from "@/OS/assistant/registry";
import {
  appendGemma4OnnxModel,
  loadGemma4ThinkingEnabled,
  saveGemma4ThinkingEnabled,
  supportsGemma4Thinking,
} from "@/lib/gemma4Onnx";

import {
  AssistantRegistry,
  loadAssistantInterfaces,
  getAssistantRegistry,
  validateToolArgs,
} from "@/OS/assistant/interfaceLoader";
import { executeTool, isMutatingTool } from "@/OS/assistant/executors";
import { useAuth } from "../hooks/useAuth";
import { makeOverrideIcon } from "./AppIcon";

const AITaskbarIcon = makeOverrideIcon("ai-maplebot", Bot);

const STORAGE_KEY = "mapleos.desktopAssistant";
const BROWSER_CONTEXT_STORAGE_KEY = "maplebrowser.lastContext";
const ASK_AI_LOCATION_STORAGE_KEY = "mapleos.askai.lastLocation";
const MAPLE_GIS_PENDING_PLACE_STORAGE_KEY = "mapleos.maplegis.pendingPlace";
const MAX_COMMAND_HISTORY = 50;
const ELECTRON_TITLEBAR_HEIGHT = 32;
const TOP_REVEAL_THRESHOLD = 6;
const HIDE_DELAY_MS = 220;
const COLLAPSED_PEEK_PX = 8;
const SPINNER_TEXT_ROTATE_MS = 1500;

const SPINNER_VERBS_CANADIAN = [
  'Pitter-Pattering',
  'Figuring-It-Out',
  'Giving-Your-Balls-A-Shake',
  'Having-A-Hard-No',
  'To-Be-Fair-ing',
  'Staying-Off-The-Laneway',
  'Pulling-A-10-4',
  'Having-A-Scrap',
  'Keeping-It-Canadian',
  'Just-Saying-So',
  'Setting-The-Tone',
  'Wheel-Snipe-Cellying',
  'Running-Em-Up-And-Filling-Em-In',
  'Never-Losing-Again',
  'Forechecking-Backchecking-Paychecking',
  'Giving-Your-Balls-A-Tug',
  'Chirping-A-Bit',
  'Taking-A-Rip',
  'Going-Top-Shelf',
  'Sniping-One',
  'Burying-It',
  'Keeping-The-Stick-On-The-Ice',
  'Dumping-And-Chasing',
  'Crashing-The-Net',
  'Playing-A-Strong-Game',
  'Letting-The-Way-She-Goes',
  'Worst-Case-Ontario-ing',
  'Not-Rocket-Appliances-ing',
  'Getting-Two-Birds-Stoned-At-Once',
  'Being-Decent',
  'Being-Greasy',
  'Making-A-Plan',
  'Sticking-To-The-Plan',
  'Losing-The-Plan',
  'Blaming-The-Plan',
  'Having-A-Drink',
  'Having-Another-Drink',
  'Still-Having-A-Drink',
  'Working-It-Out-Kinda',
  'Calling-It-Decent',
  'Getting-Ahead-By-A-Century',
  'Being-Fully-Completely',
  'Working-It-In-A-Poetic-Way',
  'Thinking-About-It-Quietly',
  'Looking-Out-At-The-Lake',
  'Letting-It-Linger',
  'Keeping-It-Subtle',
  'Just-Letting-It-Happen',
  'Apologizing-A-Little',
  'Apologizing-Again',
  'Over-Apologizing',
  'Saying-Sorry-Real-Quick',
  'Waiting-In-The-Cold',
  'Freezing-A-Bit',
  'Braving-The-Snow',
  'Shoveling-Something',
  'Warming-Up-First',
  'Putting-On-A-Jacket',
  'Putting-On-Another-Jacket',
  'Grabbing-A-Double-Double',
  'Getting-A-Timbit',
  'Finishing-The-Timbits',
  'Pouring-Some-Maple-Syrup-On-It',
  'Adding-More-Maple-Syrup',
  'Putting-Gravy-On-It',
  'Adding-Cheese-Curds',
  'Making-It-A-Poutine',
  'Having-A-Snack',
  'Having-A-Second-Snack',
  'Letting-You-Go-First',
  'Holding-The-Door',
  'Holding-It-A-Bit-Longer',
  'Waiting-Patiently',
  'Still-Waiting-Patiently',
  'Not-Making-A-Fuss',
  'Keeping-It-Polite',
  'Being-Reasonable',
  'Trying-To-Be-Nice',
  'Being-Nice-About-It',
  'Filling-Out-A-Form',
  'Submitting-The-Form',
  'Waiting-For-Approval',
  'Still-Waiting',
  'Waiting-A-Bit-Longer',
  'Processing-In-6-To-8-Weeks',
  'Processing-In-12-To-16-Weeks',
  'Definitely-Still-Processing',
  'Checking-Back-Later',
  'Following-Up-Politely',
  'Probably-Getting-It-Done',
  'Almost-Having-It',
  'Just-Giving-Er',
  'Taking-A-Quick-Rip',
  'Calling-It-Good-Enough',
  'Working-On-It-A-Bit',
  'Still-Working-On-It',
  'Working-On-It-A-Little-More',
  'Getting-There-Slowly',
  'Almost-Kinda-There',
  'Doing-Something-About-It',
  'Making-It-Happen',
  'Trying-To-Make-It-Happen',
  'Giving-It-A-Go',
  'Giving-It-Another-Go',
  'Giving-It-One-More-Go',
  'Calling-It-For-Now',
  'Coming-Back-To-It',
  'Circling-Back-To-It',
  'Leaving-It-For-Later'
];

const formatSpinnerText = (value: string) => `${value.replace(/-/g, ' ')}…`;

type ParsedToolCall = {
  tool: string;
  args: Record<string, unknown>;
};

type PendingToolExecution = {
  toolKey: string;
  args: Record<string, unknown>;
  title: string;
  description: string;
};

type AssistantAnswerBubbleAction = {
  id: string;
  label: string;
  appLabel: string;
  place: string;
};

type AssistantAnswerBubble = {
  question: string;
  answer: string;
  actions?: AssistantAnswerBubbleAction[];
};

type AssistantClarification = {
  kind: "weather_location" | "general_location";
  originalQuestion: string;
};

type BrowserContextSnapshot = {
  url?: string | null;
  updatedAt?: string | null;
};

const clampBubblePosition = (x: number, y: number, width: number, height: number) => {
  if (typeof window === "undefined") {
    return { x, y };
  }

  return {
    x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - width - 8)),
    y: Math.min(Math.max(56, y), Math.max(56, window.innerHeight - height - 8)),
  };
};

const APP_ALIASES: Record<string, string> = {
  explorer: "file-explorer",
  "file explorer": "file-explorer",
  "my computer": "file-explorer",
  files: "file-explorer",
  finder: "file-explorer",
  paint: "paint-program",
  painter: "paint-program",
  drawing: "paint-program",
  draw: "paint-program",
  notepad: "notepad",
  notes: "notepad",
  terminal: "terminal",
  cmd: "terminal",
  console: "terminal",
  browser: "web-browser",
  internet: "web-browser",
  web: "web-browser",
  calculator: "maple-calc",
  calc: "maple-calc",
  word: "maple-word",
  mapleword: "maple-word",
  docs: "maple-word",
  documents: "maple-word",
  sheet: "maple-sheet",
  spreadsheet: "maple-sheet",
  excel: "maple-sheet",
  slides: "maple-slide",
  presentation: "maple-slide",
  powerpoint: "maple-slide",
  "task manager": "task-manager",
  "ai center": "ai-center",
  "ai task manager": "ai-task-manager",
  messenger: "messenger",
  help: "help",
  search: "search",
  settings: "control-panel",
  "control panel": "control-panel",
};

const normalizeAssistantText = (value: string) =>
  value
    .toLowerCase()
    .replace(/["'.?,!]/g, " ")
    .replace(/\b(?:please|can you|could you|would you|for me|the|a|an|app|application|program)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildProgramSearchCandidates = (value: string) => {
  const normalized = normalizeAssistantText(value);
  const trimmedAction = normalized
    .replace(/^(?:open|launch|start|run|show|bring up|pull up)\s+/, "")
    .replace(/^me\s+/, "")
    .trim();

  return Array.from(new Set([normalized, trimmedAction].filter(Boolean)));
};

const isLikelyGeneralQuestion = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (trimmed.includes("?")) {
    return true;
  }

  return /^(what|what's|whats|who|who's|where|when|why|how|can|could|would|should|is|are|do|does|did|will|tell me|explain)\b/i.test(trimmed);
};

const isMapleCodeBuildRequest = (value: string) => {
  const normalized = value.toLowerCase().trim();
  if (!normalized) {
    return false;
  }

  const asksToBuild = /\b(build|create|make|develop|generate|code)\b/.test(normalized);
  const targetType = /\b(app|application|website|site|tool|game|widget|dashboard|calculator|landing page|page)\b/.test(normalized);
  const explanationOnly = /\b(how do i|how to|example|sample|tutorial|teach me|show me code|what is)\b/.test(normalized);

  return asksToBuild && targetType && !explanationOnly;
};

const isWeatherQuestion = (value: string) =>
  /\b(weather|forecast|temperature|rain|snow|sunny|cloudy|wind|windy|humid|humidity|storm|storms|precipitation)\b/i.test(value);

const isLiveInfoQuestion = (value: string) => {
  const lower = value.toLowerCase();

  return /\b(current|latest|today|tonight|tomorrow|now|recent|recently|live|breaking|this year|this season|this week|right now|202[4-9]|203\d)\b/.test(lower)
    || /\b(score|scores|standings|ranking|rankings|record|stats|statistics|schedule|schedules|playoffs|champion|championship|cup|winner|winners|best team|top team|top player|mvp)\b/.test(lower)
    || /\b(nhl|nba|nfl|mlb|ncaa|f1|formula 1|soccer|football|baseball|basketball|hockey|tennis|golf|ufc|boxing)\b/.test(lower)
    || /\b(stock|stocks|price|prices|market|markets|crypto|bitcoin|ethereum|interest rate|inflation|election|polls|news|headline|headlines)\b/.test(lower)
    || isWeatherQuestion(value);
};

const buildLiveSearchQuery = (value: string) => {

  const trimmed = value.trim();
  const currentYear = new Date().getFullYear();
  if (/\b20\d{2}\b/.test(trimmed)) {
    return trimmed;
  }

  if (isLiveInfoQuestion(trimmed)) {
    return `${trimmed} ${currentYear}`;
  }

  return trimmed;
};

const isLocationOrMapIntentQuestion = (value: string) =>
  /\b(where\s+is|map\s+of|show\s+me|visit|located|location|forecast|weather\s+in|weather\s+for|in\s+[A-Z])/i.test(value);

const sanitizeLocationCandidate = (value: string) =>
  value
    .replace(/[?!.]+$/g, "")
    .replace(/\b(?:today|tonight|tomorrow|this\s+morning|this\s+afternoon|this\s+evening|this\s+week|next\s+week|right\s+now|currently)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[,\s]+|[,\s]+$/g, "");

const isGenericPlaceCandidate = (value: string) => {
  const normalized = sanitizeLocationCandidate(value).toLowerCase();
  if (!normalized) {
    return true;
  }

  return [
    "world",
    "the world",
    "earth",
    "the earth",
    "globe",
    "the globe",
    "country",
    "largest country",
    "biggest country",
    "place",
    "location",
    "region",
    "area",
  ].includes(normalized);
};

const extractLocationFromWeatherQuestion = (value: string) => {
  const patterns = [
    /\b(?:in|for|at)\s+([^?.,!]+)/i,
    /\bweather\s+([^?.,!]+)/i,
    /\bforecast\s+([^?.,!]+)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern)?.[1];
    if (!match) continue;
    const candidate = sanitizeLocationCandidate(match);
    if (candidate && !/^the\b/i.test(candidate) && !isGenericPlaceCandidate(candidate)) {
      return candidate;
    }
  }

  return null;
};

const answerRequestsLocationContext = (value: string) => {
  const normalized = value.toLowerCase();

  return /what(?:'s| is) your location/.test(normalized)
    || /what(?:'s| is) your location in canada/.test(normalized)
    || /what area are you in/.test(normalized)
    || /which (?:city|province|region|area) are you in/.test(normalized)
    || /reply with a city, region, postal code/.test(normalized)
    || /share your (?:city|location|region|postal code)/.test(normalized)
    || /tell me (?:your|the) (?:city|location|region|postal code)/.test(normalized);
};

const extractPlaceFromQuestion = (value: string) => {
  const patterns = [
    /\bwhere\s+is\s+([^?.,!]+)/i,
    /\b(?:in|at|near|around|for)\s+([^?.,!]+)/i,
    /\b(?:about|visit|map of|show me)\s+([^?.,!]+)/i,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern)?.[1];
    if (!match) continue;
    const candidate = sanitizeLocationCandidate(match);
    if (candidate && !isGenericPlaceCandidate(candidate)) {
      return candidate;
    }
  }

  return null;
};

const extractPlaceFromAnswer = (value: string) => {
  const patterns = [
    /\bis\s+([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,3})\b/,
    /\b(?:in|at|from)\s+([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,3})\b/,
    /\b([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,3})\s+is\s+the\b/,
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern)?.[1];
    if (!match) continue;
    const candidate = sanitizeLocationCandidate(match);
    if (candidate && !isGenericPlaceCandidate(candidate)) {
      return candidate;
    }
  }

  return null;
};

const createMapleGISAction = (place: string): AssistantAnswerBubbleAction | null => {
  const normalizedPlace = sanitizeLocationCandidate(place);
  if (!normalizedPlace || isGenericPlaceCandidate(normalizedPlace)) {
    return null;
  }

  return {
    id: `maplegis:${normalizedPlace.toLowerCase()}`,
    label: `Open map for ${normalizedPlace}`,
    appLabel: "MapleGIS",
    place: normalizedPlace,
  };
};

const buildAnswerBubbleActions = (question: string, answer?: string, place?: string | null) => {
  if (!place && !isLocationOrMapIntentQuestion(question)) {
    return [] as AssistantAnswerBubbleAction[];
  }

  const resolvedPlace = place || extractPlaceFromAnswer(answer) || extractPlaceFromQuestion(question);
  if (!resolvedPlace) {
    return [] as AssistantAnswerBubbleAction[];
  }

  const mapAction = createMapleGISAction(resolvedPlace);
  return mapAction ? [mapAction] : [];
};

const readBrowserContextSnapshot = (): BrowserContextSnapshot | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = localStorage.getItem(BROWSER_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BrowserContextSnapshot | null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const inferLocationFromBrowserContext = () => {
  const snapshot = readBrowserContextSnapshot();
  if (!snapshot?.url) {
    return null;
  }

  try {
    const url = new URL(snapshot.url);
    const query = url.searchParams.get("q") || url.searchParams.get("query") || url.searchParams.get("location");
    if (!query) {
      return null;
    }

    const candidate = sanitizeLocationCandidate(
      query
        .replace(/\+/g, " ")
        .replace(/\b(weather|forecast|temperature|rain|snow|today|tonight|tomorrow|weekly|hourly|current)\b/gi, " ")
    );

    return candidate || null;
  } catch {
    return null;
  }
};

const readSavedAskAILocation = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = localStorage.getItem(ASK_AI_LOCATION_STORAGE_KEY);
    return value?.trim() || null;
  } catch {
    return null;
  }
};

const saveAskAILocation = (value: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const normalized = sanitizeLocationCandidate(value);
  if (!normalized) {
    return;
  }

  try {
    localStorage.setItem(ASK_AI_LOCATION_STORAGE_KEY, normalized);
  } catch {
  }
};

const formatWebSearchResults = (query: string, results: any) => {
  const sections: string[] = [`Live web search results for "${query}":`];

  const answerBox = results?.answer_box;
  if (answerBox) {
    const answerBits = [answerBox.title, answerBox.answer, answerBox.snippet, answerBox.snippet_highlighted_words?.join?.(", ")]
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
    if (answerBits.length > 0) {
      sections.push(`Answer box:\n${answerBits.join("\n")}`);
    }
  }

  const organicResults = Array.isArray(results?.organic_results) ? results.organic_results.slice(0, 5) : [];
  if (organicResults.length > 0) {
    sections.push(
      organicResults
        .map((result: any, index: number) => {
          const bits = [
            `${index + 1}. ${result?.title || "Untitled result"}`,
            result?.link ? `URL: ${result.link}` : null,
            result?.snippet ? `Snippet: ${result.snippet}` : null,
          ].filter(Boolean);
          return bits.join("\n");
        })
        .join("\n\n")
    );
  }

  return sections.join("\n\n");
};

const resolveProgramFromRequest = (value: string): MapleProgram | null => {
  const candidates = buildProgramSearchCandidates(value);

  for (const candidate of candidates) {
    const aliasMatch = APP_ALIASES[candidate];
    if (aliasMatch) {
      return PROGRAMS.find((program) => program.id === aliasMatch) || null;
    }
  }

  for (const candidate of candidates) {
    const exactMatch = PROGRAMS.find((program) => {
      const label = program.label.toLowerCase();
      const id = program.id.toLowerCase();
      return label === candidate || id === candidate || label.replace(/\s+/g, "") === candidate.replace(/\s+/g, "");
    });

    if (exactMatch) {
      return exactMatch;
    }
  }

  for (const candidate of candidates) {
    const partialMatch = PROGRAMS.find((program) => {
      const label = program.label.toLowerCase();
      const id = program.id.toLowerCase();
      return label.includes(candidate) || candidate.includes(label) || id.includes(candidate) || candidate.includes(id);
    });

    if (partialMatch) {
      return partialMatch;
    }
  }

  return null;
};

type DesktopAssistantProps = {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  preview?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  initialOpen?: boolean;
  persistState?: boolean;
  defaultPinned?: boolean;
  onPinnedChange?: (pinned: boolean) => void;
};

const sanitizeFileName = (name: string) =>
  String(name ?? "")
    .replace(/[<>:"/\\|?*]+/g, "")
    .trim();

const normalizeAssistantPath = (path: string) =>
  String(path ?? "")
    .replace(/\//g, "\\")
    .replace(/\\+/g, "\\")
    .trim();

const resolveDocumentsPath = async (): Promise<string> => {
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  let username = "User";
  if (user) {
    const { data: profile } = await supabase
      .from("profiles")
      .select("username,email")
      .eq("id", user.id)
      .maybeSingle();
    const profileData = (profile ?? null) as { username?: string | null; email?: string | null } | null;
    const candidate =
      profileData?.username ||
      (profileData?.email || user.email || "User").split("@")[0] ||
      "User";
    username = candidate.replace(/[^a-zA-Z0-9]/g, "") || "User";
  }
  return `C:\\User\\${username}\\Documents`;
};

const createDesktopAssistantMessage = (role: Message["role"], content: string): Message => ({
  id: `desktop-assistant-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  chatId: "desktop-assistant-tools",
  content,
  role,
  sender: role === "user" ? "user" : "ai",
  timestamp: new Date().toISOString(),
});

const buildToolSystemPrompt = (registry: AssistantRegistry) => {
  const toolLines = registry.tools
    .map((tool) => `- ${tool.key}(${tool.paramSummary}) — ${tool.description}`)
    .join("\n");

  return [
    "You are the MapleOS desktop assistant tool router.",
    "Choose at most one tool from the catalog below when it clearly matches the user's request.",
    "Respond ONLY with a JSON object shaped exactly like {\"tool\":\"appId.actionId\",\"args\":{...}}.",
    "If no listed tool applies, respond with NONE.",
    "Do not include markdown, code fences, explanations, or extra text.",
    "Prefer concise arguments and only include fields that the tool needs.",
    "Tool catalog:",
    toolLines,
  ].join("\n");
};

const parseToolCallResponse = (content: string): ParsedToolCall | "NONE" | null => {
  const trimmed = content.trim();
  if (!trimmed) return null;

  const candidates = new Set<string>([trimmed]);
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1]?.trim();
  if (fenced) {
    candidates.add(fenced);
  }

  for (const candidate of Array.from(candidates)) {
    if (/^none$/i.test(candidate.trim())) {
      return "NONE";
    }

    const firstBrace = candidate.indexOf("{");
    const lastBrace = candidate.lastIndexOf("}");
    const jsonCandidate = firstBrace >= 0 && lastBrace > firstBrace
      ? candidate.slice(firstBrace, lastBrace + 1)
      : candidate;

    try {
      const parsed = JSON.parse(jsonCandidate) as { tool?: unknown; args?: unknown };
      if (typeof parsed.tool === "string") {
        return {
          tool: parsed.tool,
          args: typeof parsed.args === "object" && parsed.args !== null && !Array.isArray(parsed.args)
            ? (parsed.args as Record<string, unknown>)
            : {},
        };
      }
    } catch {
      continue;
    }
  }

  return null;
};

const DesktopAssistant: React.FC<DesktopAssistantProps> = ({
  onOpenWindow,
  preview = false,
  initialOpen = false,
  persistState = true,
  defaultPinned = false,
  onPinnedChange,
}) => {
  const { toast } = useToast();
  const { models, fetchModels } = useSupabaseStore();
  const { user: appUser } = useAppStore();
  const { user: osUser } = useAuth();
  const [safeMode, setSafeMode] = useState(true);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockedMsg, setBlockedMsg] = useState<string | null>(null);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [draftInput, setDraftInput] = useState("");
  const [profileDefaultModelApiName, setProfileDefaultModelApiName] = useState<string | null>(null);
  const [assistantRegistry, setAssistantRegistry] = useState<AssistantRegistry | null>(null);
  const [toolingError, setToolingError] = useState<string | null>(null);
  const [pendingToolExecution, setPendingToolExecution] = useState<PendingToolExecution | null>(null);
  const [pinned, setPinned] = useState(defaultPinned);
  const [expanded, setExpanded] = useState(initialOpen || defaultPinned);
  const [spinnerText, setSpinnerText] = useState(() => formatSpinnerText(SPINNER_VERBS_CANADIAN[0]));
  const [answerBubble, setAnswerBubble] = useState<AssistantAnswerBubble | null>(null);
  const [pendingClarification, setPendingClarification] = useState<AssistantClarification | null>(null);
  const [bubbleReply, setBubbleReply] = useState("");
  const [bubblePosition, setBubblePosition] = useState<{ x: number; y: number } | null>(null);
  const [answerCopied, setAnswerCopied] = useState(false);
  const [gemmaThinkingEnabled, setGemmaThinkingEnabled] = useState(() => loadGemma4ThinkingEnabled());
  const isElectron = typeof window !== "undefined" && Boolean(window.electronAPI?.isElectron);
  const [electronFullScreen, setElectronFullScreen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const autoHideRef = useRef<number | null>(null);
  const bubbleReplyRef = useRef<HTMLInputElement>(null);
  const answerBubbleRef = useRef<HTMLDivElement>(null);
  const answerCopiedTimeoutRef = useRef<number | null>(null);

  const clearAutoHide = useCallback(() => {
    if (autoHideRef.current) {
      window.clearTimeout(autoHideRef.current);
      autoHideRef.current = null;
    }
  }, []);

  const resetAnswerCopiedState = useCallback(() => {
    if (answerCopiedTimeoutRef.current) {
      window.clearTimeout(answerCopiedTimeoutRef.current);
      answerCopiedTimeoutRef.current = null;
    }
    setAnswerCopied(false);
  }, []);

  const handleCopyAnswerBubble = useCallback(async () => {
    if (!answerBubble?.answer) return;

    try {
      await navigator.clipboard.writeText(answerBubble.answer);
      resetAnswerCopiedState();
      setAnswerCopied(true);
      answerCopiedTimeoutRef.current = window.setTimeout(() => {
        setAnswerCopied(false);
        answerCopiedTimeoutRef.current = null;
      }, 2000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Unable to copy the AI response right now.",
        variant: "destructive",
      });
    }
  }, [answerBubble, resetAnswerCopiedState, toast]);

  const revealBar = useCallback((focusInput = false) => {
    clearAutoHide();
    setExpanded(true);

    if (focusInput) {
      window.setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [clearAutoHide]);

  const scheduleAutoHide = useCallback(() => {
    if (preview || pinned) {
      return;
    }

    clearAutoHide();
    autoHideRef.current = window.setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement === inputRef.current || barRef.current?.contains(activeElement)) {
        return;
      }
      setExpanded(false);
    }, HIDE_DELAY_MS);
  }, [clearAutoHide, pinned, preview]);

  const maybeCloseAfterAction = useCallback(() => {
    setInput("");
    setHistoryIndex(-1);
    setDraftInput("");
    if (!pinned) {
      scheduleAutoHide();
    }
  }, [pinned, scheduleAutoHide]);

  useEffect(() => {
    const handler = (ev: Event) => {
      const d = (ev as CustomEvent<any>).detail;
      if (!d?.path) return;
      setBlockedMsg(`Agent access blocked for ${d.path}; update your allowlist in System Graph.`);
      window.setTimeout(() => setBlockedMsg(null), 3500);
    };
    window.addEventListener("agent:blocked", handler as EventListener);
    return () => window.removeEventListener("agent:blocked", handler as EventListener);
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadAssistantInterfaces()
      .then(() => {
        if (cancelled) return;
        setAssistantRegistry(getAssistantRegistry());
        setToolingError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setAssistantRegistry(null);
        setToolingError(error instanceof Error ? error.message : "Failed to load assistant capabilities.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProfileDefaultModel = async () => {
      if (!osUser?.id) {
        setProfileDefaultModelApiName(null);
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("default_model")
        .eq("id", osUser.id)
        .maybeSingle();
      const profileRow = (data ?? null) as { default_model?: string | null } | null;

      if (!cancelled) {
        setProfileDefaultModelApiName(typeof profileRow?.default_model === "string" ? profileRow.default_model : null);
      }
    };

    loadProfileDefaultModel();
    return () => {
      cancelled = true;
    };
  }, [osUser?.id]);

  useEffect(() => {
    if (!persistState) {
      setPinned(defaultPinned);
      setExpanded(initialOpen || defaultPinned);
      return;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setPinned(defaultPinned);
        setExpanded(initialOpen || defaultPinned);
        return;
      }

      const parsed = JSON.parse(raw);
      const nextPinned = Boolean(parsed.pinned ?? defaultPinned);
      setPinned(nextPinned);
      setExpanded(nextPinned || Boolean(parsed.expanded ?? initialOpen));
      setCommandHistory(Array.isArray(parsed.commandHistory) ? parsed.commandHistory.slice(-MAX_COMMAND_HISTORY) : []);
    } catch {
      setPinned(defaultPinned);
      setExpanded(initialOpen || defaultPinned);
    }
  }, [defaultPinned, initialOpen, persistState]);

  useEffect(() => {
    if (!persistState) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        pinned,
        expanded,
        commandHistory: commandHistory.slice(-MAX_COMMAND_HISTORY),
      })
    );
  }, [commandHistory, expanded, persistState, pinned]);

  useEffect(() => {
    if (!isElectron || !window.electronAPI?.windowIsFullScreen) {
      setElectronFullScreen(false);
      return;
    }

    let cancelled = false;

    const syncFullScreenState = async () => {
      try {
        const nextValue = await window.electronAPI.windowIsFullScreen();
        if (!cancelled) {
          setElectronFullScreen(Boolean(nextValue));
        }
      } catch {
        if (!cancelled) {
          setElectronFullScreen(false);
        }
      }
    };

    void syncFullScreenState();
    const interval = window.setInterval(syncFullScreenState, 500);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isElectron]);

  useEffect(() => {
    onPinnedChange?.(pinned);
    window.dispatchEvent(new CustomEvent("desktopAssistant:pinnedChange", { detail: { pinned } }));
  }, [onPinnedChange, pinned]);

  useEffect(() => {
    if (pinned) {
      clearAutoHide();
      setExpanded(true);
    }
  }, [clearAutoHide, pinned]);

  useEffect(() => {
    if (!models || models.length === 0) {
      fetchModels().catch(() => {});
    }
  }, [models, fetchModels]);

  const availableModels = useMemo(() => appendGemma4OnnxModel(Array.isArray(models) ? models : []), [models]);

  const selectedDefaultModel: AIModel | null = useMemo(() => {
    if (availableModels.length === 0) {
      return null;
    }

    const preferredApiName = appUser?.defaultModelApiName || profileDefaultModelApiName;
    if (preferredApiName) {
      const found = availableModels.find((model) => model.apiName === preferredApiName && model.is_active !== false);
      if (found) {
        return found;
      }
    }

    const llama3Model = availableModels.find(
      (model) => model.is_active !== false && (model.apiName === "llama3" || model.name.toLowerCase().includes("llama3"))
    );
    if (llama3Model) {
      return llama3Model;
    }

    return availableModels.find((model) => model.is_active !== false && model.tier === "free") || availableModels.find((model) => model.is_active !== false) || null;
  }, [appUser, availableModels, profileDefaultModelApiName]);

  const gemmaThinkingAvailable = supportsGemma4Thinking(selectedDefaultModel);
  const gemmaThinkingConfig: AIConfig | undefined = gemmaThinkingAvailable && gemmaThinkingEnabled
    ? { enableThinking: true, max_tokens: 1024 }
    : undefined;
  const visibleSwitchClassName = 'border-slate-300 data-[state=unchecked]:bg-slate-200 dark:border-slate-600 dark:data-[state=unchecked]:bg-slate-700';
  const electronTitlebarInset = isElectron && !electronFullScreen ? ELECTRON_TITLEBAR_HEIGHT : 0;
  const revealZoneHeight = TOP_REVEAL_THRESHOLD + 4;
  const revealThreshold = electronTitlebarInset + revealZoneHeight;

  useEffect(() => {

    const toggle = () => {
      if (pinned) {
        setExpanded(true);
        return;
      }
      setExpanded((prev) => !prev);
    };
    const openEvt = () => revealBar(true);
    const closeEvt = () => {
      if (!pinned) {
        clearAutoHide();
        setExpanded(false);
      }
    };

    window.addEventListener("desktopAssistant:toggle", toggle as EventListener);
    window.addEventListener("desktopAssistant:open", openEvt as EventListener);
    window.addEventListener("desktopAssistant:close", closeEvt as EventListener);
    return () => {
      window.removeEventListener("desktopAssistant:toggle", toggle as EventListener);
      window.removeEventListener("desktopAssistant:open", openEvt as EventListener);
      window.removeEventListener("desktopAssistant:close", closeEvt as EventListener);
    };
  }, [clearAutoHide, pinned, revealBar]);

  useEffect(() => {
    if (preview || pinned) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (event.clientY >= electronTitlebarInset && event.clientY <= revealThreshold) {
        revealBar(false);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [electronTitlebarInset, pinned, preview, revealBar, revealThreshold]);

  useEffect(() => {
    if (expanded) {
      window.setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [expanded]);

  useEffect(() => {
    if (pendingClarification) {
      window.setTimeout(() => bubbleReplyRef.current?.focus(), 60);
    } else {
      setBubbleReply("");
    }
  }, [pendingClarification]);

  useEffect(() => {
    if (!answerBubble) {
      setBubblePosition(null);
    }
  }, [answerBubble]);

  useEffect(() => {
    if (!busy) {
      setSpinnerText(formatSpinnerText(SPINNER_VERBS_CANADIAN[0]));
      return;
    }

    const pickSpinnerText = () => {
      const next = SPINNER_VERBS_CANADIAN[Math.floor(Math.random() * SPINNER_VERBS_CANADIAN.length)] || SPINNER_VERBS_CANADIAN[0];
      setSpinnerText(formatSpinnerText(next));
    };

    pickSpinnerText();
    const intervalId = window.setInterval(pickSpinnerText, SPINNER_TEXT_ROTATE_MS);
    return () => window.clearInterval(intervalId);
  }, [busy]);

  useEffect(() => () => clearAutoHide(), [clearAutoHide]);

  useEffect(() => {
    return () => {
      if (answerCopiedTimeoutRef.current) {
        window.clearTimeout(answerCopiedTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    resetAnswerCopiedState();
  }, [answerBubble, resetAnswerCopiedState]);

  const openApp = useCallback((appId: string, title?: string, filePath?: string, content?: string) => {
    if (!onOpenWindow) return;
    onOpenWindow(appId, title || appId, filePath, content);
  }, [onOpenWindow]);

  const wallpaperMap: Record<string, string> = {
    default: getAssetPath("/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg"),
    edmonton: getAssetPath("/os/assets/images/wallpapers/edmonton_canada_night_city_118462_1600x900.jpg"),
    owl: getAssetPath("/os/assets/images/wallpapers/owl_white_owl_polar_owl_136287_1600x900.jpg"),
    toronto: getAssetPath("/os/assets/images/wallpapers/city_buildings_architecture_160579_1600x900.jpg"),
    deer: getAssetPath("/os/assets/images/wallpapers/deer_wildlife_horns_131368_1600x900.jpg"),
    cubes: getAssetPath("/os/assets/images/wallpapers/cubes_edges_abstraction_1312232_1600x900.jpg"),
  };

  const rememberCommand = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setCommandHistory((prev) => {
      const next = prev.filter((item) => item !== trimmed);
      next.push(trimmed);
      return next.slice(-MAX_COMMAND_HISTORY);
    });
    setHistoryIndex(-1);
    setDraftInput("");
  }, []);

  const refreshCreditsDisplay = useCallback(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("mapleos:credits-refresh"));
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mapleos:credits-refresh"));
    }, 750);
  }, []);

  const showAnswerBubble = useCallback((question: string, answer: string, place?: string | null) => {
    setAnswerBubble({
      question,
      answer,
      actions: buildAnswerBubbleActions(question, answer, place),
    });
  }, []);

  const openPlaceInMapleGIS = useCallback((place: string) => {
    const normalizedPlace = sanitizeLocationCandidate(place);
    if (!normalizedPlace) {
      return;
    }

    try {
      localStorage.setItem(MAPLE_GIS_PENDING_PLACE_STORAGE_KEY, normalizedPlace);
    } catch {
    }

    openApp("maple-gis", "MapleGIS");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("maplegis:focus-place", { detail: { place: normalizedPlace } }));
    }, 150);
  }, [openApp]);

  const startBubbleDrag = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!answerBubbleRef.current) {
      return;
    }

    const rect = answerBubbleRef.current.getBoundingClientRect();
    const originX = event.clientX;
    const originY = event.clientY;
    const offsetX = rect.left;
    const offsetY = rect.top;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const nextX = offsetX + (moveEvent.clientX - originX);
      const nextY = offsetY + (moveEvent.clientY - originY);
      setBubblePosition(clampBubblePosition(nextX, nextY, rect.width, rect.height));
    };

    const handleMouseUp = () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, []);

  const notifyPathChanged = useCallback((path: string) => {
    if (!path) return;
    window.dispatchEvent(new CustomEvent("fileSystemChanged", { detail: { path } }));
  }, []);

  const createAssistantFolder = useCallback(async (path: string) => {
    const normalizedPath = normalizeAssistantPath(path);
    const created = await globalNavigator.createFolder(normalizedPath);
    if (created) {
      notifyPathChanged(globalNavigator.getParentPath(normalizedPath));
      notifyPathChanged(normalizedPath);
    }
    return created;
  }, [notifyPathChanged]);

  const createAssistantFile = useCallback(async (path: string, content = "") => {
    const normalizedPath = normalizeAssistantPath(path);
    const created = await globalNavigator.createFile(normalizedPath, content);
    if (created) {
      notifyPathChanged(globalNavigator.getParentPath(normalizedPath));
      notifyPathChanged(normalizedPath);
    }
    return created;
  }, [notifyPathChanged]);

  const readAssistantFile = useCallback(async (path: string) => {
    const normalizedPath = normalizeAssistantPath(path);
    await globalNavigator.waitForInitialization();
    await globalNavigator.syncWithVFS();
    return globalNavigator.findItemByPath(normalizedPath)?.content ?? null;
  }, []);

  const writeAssistantFile = useCallback(async (path: string, content: string) => {
    const normalizedPath = normalizeAssistantPath(path);
    const updated = await globalNavigator.updateFileContent(normalizedPath, content);
    const saved = updated || await globalNavigator.createFile(normalizedPath, content);
    if (saved) {
      notifyPathChanged(globalNavigator.getParentPath(normalizedPath));
      notifyPathChanged(normalizedPath);
    }
    return saved;
  }, [notifyPathChanged]);

  const runStructuredTool = useCallback(async (toolKey: string, args: Record<string, unknown>) => {
    if (isOSAIBlocked() && isMutatingTool(toolKey)) {
      throw new Error("AI OS access is blocked right now. Resume AI access before creating or modifying files.");
    }

    const result = await executeTool(toolKey, args, {
      openApp: (appId, title, filePath, content) => openApp(appId, title, filePath, content),
      ensureDir,
      createFolder: createAssistantFolder,
      createFile: createAssistantFile,
      readFile: readAssistantFile,
      writeFile: writeAssistantFile,
      pathExists: (path) => globalNavigator.pathExists(normalizeAssistantPath(path)),
      resolveDocumentsPath,
    });

    toast({
      title: "Action completed",
      description: result.message,
    });

    if (result.shouldClose !== false) {
      maybeCloseAfterAction();
    }
  }, [createAssistantFile, createAssistantFolder, maybeCloseAfterAction, openApp, readAssistantFile, toast, writeAssistantFile]);

  const tryStructuredToolCall = useCallback(async (text: string) => {
    if (!assistantRegistry || !selectedDefaultModel || assistantRegistry.tools.length === 0) {
      return false;
    }

    const modelMessages = [
      createDesktopAssistantMessage("system", buildToolSystemPrompt(assistantRegistry)),
      createDesktopAssistantMessage("user", text),
    ];

    const { content } = await sendMessageToModel(
      modelMessages,
      selectedDefaultModel,
      availableModels,
      gemmaThinkingConfig
    );

    refreshCreditsDisplay();

    const parsed = parseToolCallResponse(content ?? "");
    if (!parsed || parsed === "NONE") {
      return false;
    }

    const validationErrors = validateToolArgs(parsed.tool, parsed.args, assistantRegistry);
    if (validationErrors.length > 0) {
      return false;
    }

    const registryEntry = assistantRegistry.actionsByKey[parsed.tool];
    if (!registryEntry) {
      return false;
    }

    if (registryEntry.tool.requiresConfirmation || safeMode) {
      setPendingToolExecution({
        toolKey: parsed.tool,
        args: parsed.args,
        title: `${registryEntry.app.name} · ${registryEntry.action.title}`,
        description: registryEntry.action.description,
      });
      return true;
    }

    await runStructuredTool(parsed.tool, parsed.args);
    return true;
  }, [assistantRegistry, availableModels, gemmaThinkingConfig, refreshCreditsDisplay, runStructuredTool, safeMode, selectedDefaultModel]);

  const askGeneralQuestion = useCallback(async (question: string) => {
    if (!selectedDefaultModel) {
      return null;
    }

    if (isLiveInfoQuestion(question)) {
      const searchQuery = buildLiveSearchQuery(question);
      const { data, error } = await supabase.functions.invoke("web-search", { body: { query: searchQuery } });
      if (error) {
        throw new Error(error.message || "Unable to search the web right now.");
      }

      const searchSummary = formatWebSearchResults(searchQuery, data?.results);
      const { content } = await sendMessageToModel(
        [
          createDesktopAssistantMessage(
            "system",
            "You are Ask AI inside MapleOS. The user is asking a current or time-sensitive question. Use the live web search results provided to answer accurately and concisely. Prefer the most recent information from the evidence. If the evidence is mixed, say that clearly."
          ),
          createDesktopAssistantMessage(
            "user",
            [
              `Original question: ${question}`,
              `Live search query: ${searchQuery}`,
              `Search evidence:\n${searchSummary}`,
            ].join("\n\n")
          ),
        ],
        selectedDefaultModel,
        availableModels,
        gemmaThinkingConfig
      );

      refreshCreditsDisplay();
      return content?.trim() || searchSummary;
    }

    const { content } = await sendMessageToModel(
      [
        createDesktopAssistantMessage(
          "system",
          "You are Ask AI inside MapleOS. Answer the user's question directly in plain text. Be concise and helpful. If the question depends on live or real-time information you cannot verify, say that clearly instead of pretending certainty, then offer the best guidance you can."
        ),
        createDesktopAssistantMessage("user", question),
      ],
      selectedDefaultModel,
      availableModels,
      gemmaThinkingConfig
    );

    refreshCreditsDisplay();
    const answer = content?.trim();
    return answer || null;
  }, [availableModels, gemmaThinkingConfig, refreshCreditsDisplay, selectedDefaultModel]);

  const askGeneralQuestionWithLocation = useCallback(async (question: string, location: string) => {
    const normalizedLocation = sanitizeLocationCandidate(location);
    if (!selectedDefaultModel || !normalizedLocation) {
      return null;
    }

    const { content } = await sendMessageToModel(
      [
        createDesktopAssistantMessage(
          "system",
          "You are Ask AI inside MapleOS. Answer the user's question directly in plain text. Use the provided location context to answer specifically for that area instead of asking for location again. Be concise and helpful."
        ),
        createDesktopAssistantMessage(
          "user",
          [
            `Original question: ${question}`,
            `Location context: ${normalizedLocation}`,
            `Answer specifically for ${normalizedLocation}.`,
          ].join("\n\n")
        ),
      ],
      selectedDefaultModel,
      availableModels,
      gemmaThinkingConfig
    );

    refreshCreditsDisplay();
    return content?.trim() || null;
  }, [availableModels, gemmaThinkingConfig, refreshCreditsDisplay, selectedDefaultModel]);

  const askWeatherQuestion = useCallback(async (question: string, location: string) => {
    const normalizedLocation = sanitizeLocationCandidate(location);
    if (!normalizedLocation) {
      return null;
    }

    const queryHasLocation = question.toLowerCase().includes(normalizedLocation.toLowerCase());
    const searchQuery = queryHasLocation ? question : `${question.replace(/[?!.]+$/g, "")} in ${normalizedLocation}`;
    const { data, error } = await supabase.functions.invoke("web-search", { body: { query: searchQuery } });
    if (error) {
      throw new Error(error.message || "Unable to search the web right now.");
    }

    const searchSummary = formatWebSearchResults(searchQuery, data?.results);
    if (!selectedDefaultModel) {
      return searchSummary;
    }

    const browserContext = readBrowserContextSnapshot();
    const { content } = await sendMessageToModel(
      [
        createDesktopAssistantMessage(
          "system",
          "You are Ask AI inside MapleOS. Use the live web search results provided to answer the user's weather question. Be concise, practical, and honest about uncertainty. Prefer a short summary followed by 2-4 brief bullet points."
        ),
        createDesktopAssistantMessage(
          "user",
          [
            `Original question: ${question}`,
            `Location: ${normalizedLocation}`,
            browserContext?.url ? `Recent Maple Browser URL: ${browserContext.url}` : null,
            `Search evidence:\n${searchSummary}`,
          ].filter(Boolean).join("\n\n")
        ),
      ],
      selectedDefaultModel,
      availableModels,
      gemmaThinkingConfig
    );

    refreshCreditsDisplay();
    return content?.trim() || searchSummary;
  }, [availableModels, gemmaThinkingConfig, refreshCreditsDisplay, selectedDefaultModel]);

  const handleCommand = async (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    const t = text.toLowerCase();
    setBusy(true);

    try {
      rememberCommand(text);

      if (pendingClarification?.kind === "weather_location") {
        if (/^(cancel|stop|never mind|nevermind)$/i.test(text)) {
          setPendingClarification(null);
          showAnswerBubble(
            pendingClarification.originalQuestion,
            "Okay — I won't look up the weather right now."
          );
          maybeCloseAfterAction();
          return;
        }

        const weatherAnswer = await askWeatherQuestion(pendingClarification.originalQuestion, text);
        saveAskAILocation(text);
        setPendingClarification(null);
        if (weatherAnswer) {
          showAnswerBubble(
            `${pendingClarification.originalQuestion} (${sanitizeLocationCandidate(text)})`,
            weatherAnswer,
            text
          );
          maybeCloseAfterAction();
          return;
        }
      }

      if (pendingClarification?.kind === "general_location") {
        if (/^(cancel|stop|never mind|nevermind)$/i.test(text)) {
          setPendingClarification(null);
          showAnswerBubble(
            pendingClarification.originalQuestion,
            "Okay — I won't use a location for that right now."
          );
          maybeCloseAfterAction();
          return;
        }

        const locationAnswer = await askGeneralQuestionWithLocation(pendingClarification.originalQuestion, text);
        saveAskAILocation(text);
        setPendingClarification(null);
        if (locationAnswer) {
          showAnswerBubble(
            `${pendingClarification.originalQuestion} (${sanitizeLocationCandidate(text)})`,
            locationAnswer,
            text
          );
          maybeCloseAfterAction();
          return;
        }
      }

      if (
        /open\s+(a\s+)?(word|maple\s*word)\s+document.*(write|story|about)/.test(t) ||
        /\b(canada\s+story|story\s+about\s+canada|write\s+about\s+canada)\b/.test(t)
      ) {
        let title = "Canada Story";
        const titleMatch = text.match(/called\s+["']?([^"']+)["']?/i) || text.match(/named\s+["']?([^"']+)["']?/i);
        if (titleMatch?.[1]) title = sanitizeFileName(titleMatch[1]);

        let content = `# ${title}\n\nCanada is a vast and beautiful country, known for its diverse landscapes, vibrant cities, and welcoming people.\n\n`;
        if (selectedDefaultModel) {
          const systemPrompt = "You are helping a user by writing a short, friendly story. Return plain text only.";
          const userPrompt = `Write a short story about Canada. Title: ${title}. Keep it friendly, 4–7 paragraphs.`;
          try {
            const { content: aiOut } = await sendMessageToModel(
              [
                createDesktopAssistantMessage("system", systemPrompt),
                createDesktopAssistantMessage("user", userPrompt),
              ],
              selectedDefaultModel,
              availableModels,
              gemmaThinkingConfig
            );
            if (aiOut?.trim()) content = aiOut.trim();
            refreshCreditsDisplay();
          } catch {
          }
        }

        const docs = await resolveDocumentsPath();
        const dir = `${docs}\\MapleWord`;
        const fileName = `${sanitizeFileName(title)}.mword`;
        const filePath = `${dir}\\${fileName}`;

        await ensureDir(dir);
        await createAssistantFolder(dir);
        await createAssistantFile(filePath, content);

        openApp("maple-word", `MapleWord - ${fileName}`, filePath, content);
        maybeCloseAfterAction();
        return;
      }

      if (isMapleCodeBuildRequest(text)) {
        openApp("terminal", "Terminal - MapleCode", "__maplecode__", text);
        maybeCloseAfterAction();
        return;
      }

      if (/\b(?:can\s+you\s+)?(?:play|open|launch|start)\s+(?:the\s+)?pac[\s-]?man\b/.test(t) || /\bpac[\s-]?man\b.*\b(?:play|runner)\b/.test(t)) {
        openApp("pacman-runner", "Pac-Man Runner");
        maybeCloseAfterAction();
        return;
      }

      if (assistantRegistry) {
        try {
          const handledByTool = await tryStructuredToolCall(text);
          if (handledByTool) {
            return;
          }
        } catch {
        }
      }

      const assistantActionResult = await runAssistantAppCommand(text, {
        openApp: (appId, title, filePath, content) => openApp(appId, title, filePath, content),
        createFolder: createAssistantFolder,
        createFile: createAssistantFile,
        ensureDir,
        pathExists: (path) => globalNavigator.pathExists(normalizeAssistantPath(path)),
        resolveDocumentsPath,
      });

      if (assistantActionResult.matched) {
        if (assistantActionResult.success === false) {
          toast({ title: "I couldn't complete that", description: assistantActionResult.error, variant: "destructive" });
        } else if (assistantActionResult.shouldClose !== false) {
          maybeCloseAfterAction();
        }
        return;
      }

      if (/^open\s+(ai\s*center|ai\s*hub|ai\s*panel)$/.test(t)) {
        openApp("ai-center", "AI Center");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+(chat|ai\s*chat)$/.test(t)) {
        openApp("canxp-module", "AI Chat", "/chat");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+settings.*(models|providers)/.test(t)) {
        openApp("canxp-module", "Settings", "/settings");
        maybeCloseAfterAction();
        return;
      }

      if (/arrange\s+icons.*name/.test(t)) {
        window.dispatchEvent(new CustomEvent("desktop:arrangeIcons", { detail: { sortBy: "name" } }));
        maybeCloseAfterAction();
        return;
      }
      if (/arrange\s+icons.*date/.test(t)) {
        window.dispatchEvent(new CustomEvent("desktop:arrangeIcons", { detail: { sortBy: "date" } }));
        maybeCloseAfterAction();
        return;
      }

      if (/change\s+wallpaper\s+to\s+([a-z\s]+)/.test(t)) {
        const m = t.match(/change\s+wallpaper\s+to\s+([a-z\s]+)/);
        const key = (m?.[1] || "").trim().replace(/\s+/g, "").toLowerCase();
        const url = wallpaperMap[key];
        if (url) {
          window.dispatchEvent(new CustomEvent("mapleos:setWallpaper", { detail: { url } }));
          maybeCloseAfterAction();
        } else {
          toast({ title: "Unknown wallpaper", description: "Try default, edmonton, owl, toronto, deer, cubes", variant: "destructive" });
        }
        return;
      }

      if (/^(stop|disable)\s+ai.*(access|writes?)/.test(t) || /^stop\s+ai$/.test(t)) {
        setOSAIBlocked(true);
        maybeCloseAfterAction();
        return;
      }
      if (/^(resume|enable)\s+ai.*(access|writes?)/.test(t) || /^resume\s+ai$/.test(t)) {
        setOSAIBlocked(false);
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+(notepad|notes?)$/.test(t)) {
        openApp("notepad", "Notepad");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+(paint|painter|drawing app|draw)$/.test(t)) {
        openApp("paint-program", "Paint");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+(my\s+computer|computer|explorer|file\s+explorer|files)$/.test(t)) {
        openApp("file-explorer", "File Explorer");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+task\s+manager$/.test(t)) {
        openApp("task-manager", "Task Manager");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+browser$/.test(t)) {
        openApp("web-browser", "Navigator");
        maybeCloseAfterAction();
        return;
      }

      if (/^open\s+terminal.*(list|show).*(desktop)/.test(t)) {
        openApp("terminal", "Terminal");
        maybeCloseAfterAction();
        return;
      }

      const resolvedProgram = resolveProgramFromRequest(text);
      if (resolvedProgram && /\b(open|launch|start|run|show|bring up|pull up)\b/i.test(text)) {
        openApp(resolvedProgram.id, resolvedProgram.label);
        maybeCloseAfterAction();
        return;
      }

      if (isWeatherQuestion(text)) {
        const explicitLocation = extractLocationFromWeatherQuestion(text);
        const inferredBrowserLocation = explicitLocation ? null : inferLocationFromBrowserContext();
        const savedLocation = explicitLocation || inferredBrowserLocation ? null : readSavedAskAILocation();
        const resolvedLocation = explicitLocation || inferredBrowserLocation || savedLocation;

        if (!resolvedLocation) {
          setPendingClarification({ kind: "weather_location", originalQuestion: text });
          showAnswerBubble(
            text,
            "What location should I use for that forecast? Reply with a city, region, postal code, or say \"cancel\"."
          );
          maybeCloseAfterAction();
          return;
        }

        const weatherAnswer = await askWeatherQuestion(text, resolvedLocation);
        saveAskAILocation(resolvedLocation);
        if (weatherAnswer) {
          showAnswerBubble(
            explicitLocation ? text : `${text} (${resolvedLocation})`,
            weatherAnswer,
            resolvedLocation
          );
          maybeCloseAfterAction();
          return;
        }
      }

      if (isLikelyGeneralQuestion(text)) {
        try {
          const answer = await askGeneralQuestion(text);
          if (answer) {
            if (answerRequestsLocationContext(answer)) {
              setPendingClarification({ kind: "general_location", originalQuestion: text });
            }
            showAnswerBubble(text, answer);
            maybeCloseAfterAction();
            return;
          }
        } catch {
        }
      }

      const suggestions = buildProgramSearchCandidates(text)
        .flatMap((candidate) =>
          PROGRAMS.filter((program) => {
            const label = program.label.toLowerCase();
            const id = program.id.toLowerCase();
            return label.includes(candidate) || id.includes(candidate) || candidate.includes(label);
          })
        )
        .filter((program, index, list) => list.findIndex((item) => item.id === program.id) === index)
        .slice(0, 3)
        .map((program) => `"Open ${program.label}"`);

      toast({
        title: "I couldn't match that yet",
        description: suggestions.length > 0
          ? `Try ${suggestions.join(", ")}.`
          : "Try: \"Open Paint\", \"Open Task Manager\", \"Open My Computer\", or \"Open AI Center\".",
      });
    } finally {
      setBusy(false);
    }
  };

  const executePendingTool = useCallback(async () => {
    if (!pendingToolExecution) return;

    setBusy(true);
    try {
      await runStructuredTool(pendingToolExecution.toolKey, pendingToolExecution.args);
    } catch (error) {
      toast({
        title: "Action failed",
        description: error instanceof Error ? error.message : "The requested action could not be completed.",
        variant: "destructive",
      });
    } finally {
      setPendingToolExecution(null);
      setBusy(false);
    }
  }, [pendingToolExecution, runStructuredTool, toast]);

  const visible = preview || pinned || expanded;
  const frameHeight = visible ? 48 : revealZoneHeight;

  return (

    <>
      <AlertDialog open={Boolean(pendingToolExecution)} onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setPendingToolExecution(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingToolExecution?.title || "Confirm action"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingToolExecution?.description || "Review this assistant action before continuing."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border bg-muted/40 p-3">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Parameters</div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs text-foreground">
              {pendingToolExecution ? JSON.stringify(pendingToolExecution.args, null, 2) : "{}"}
            </pre>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(event) => {
              event.preventDefault();
              void executePendingTool();
            }}>
              {busy ? "Running…" : "Run action"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className="relative z-40 overflow-hidden pointer-events-none transition-[height] duration-300 ease-out"
        style={{ height: frameHeight }}
      >
        {!preview && !pinned && (
          <div
            className="absolute inset-x-0 top-0 z-10 pointer-events-auto"
            style={{ height: revealZoneHeight }}
            onMouseEnter={() => revealBar(false)}
          />
        )}

        <div
          ref={barRef}
          className="absolute inset-x-0 top-0 pointer-events-auto transition-transform duration-300 ease-out"
          style={{
            transform: visible ? "translateY(0)" : "translateY(-100%)",
          }}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.stopPropagation()}
          onMouseEnter={clearAutoHide}
          onMouseLeave={scheduleAutoHide}
          onFocusCapture={() => revealBar(false)}
          onBlurCapture={(event) => {
            const nextFocused = event.relatedTarget as Node | null;
            if (!barRef.current?.contains(nextFocused)) {
              scheduleAutoHide();
            }
          }}
        >
          <TooltipProvider>
            <Card className="rounded-none border-x-0 border-t-0 bg-white text-taskbar-foreground shadow-lg dark:bg-taskbar">

              <div className="flex h-12 items-center gap-2 px-3">
                <div className="flex min-w-0 items-center gap-2">
                  <AITaskbarIcon className="h-5 w-5 shrink-0 text-current" />

                  <span className="text-sm font-medium whitespace-nowrap">Ask AI</span>

                </div>

                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Input
                    ref={inputRef}
                    type="search"
                    name="mapleos-ai-command"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    value={input}
                    onChange={(e) => {
                      setHistoryIndex(-1);
                      setDraftInput(e.target.value);
                      setInput(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        if (!commandHistory.length) return;
                        const nextIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
                        if (historyIndex === -1) {
                          setDraftInput(input);
                        }
                        setHistoryIndex(nextIndex);
                        setInput(commandHistory[nextIndex] ?? "");
                        return;
                      }

                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        if (!commandHistory.length) return;
                        if (historyIndex === -1) return;

                        const nextIndex = historyIndex + 1;
                        if (nextIndex >= commandHistory.length) {
                          setHistoryIndex(-1);
                          setInput(draftInput);
                          return;
                        }

                        setHistoryIndex(nextIndex);
                        setInput(commandHistory[nextIndex] ?? "");
                        return;
                      }

                      if (e.key === "Escape" && !pinned) {
                        e.preventDefault();
                        setExpanded(false);
                        return;
                      }

                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (busy) return;
                        void handleCommand(input);
                      }
                    }}
                    placeholder='Ask me anything...'
                    className={busy
                      ? "h-9 min-w-0 border-border/60 bg-white text-foreground lg:max-w-[26rem] dark:bg-background/90"
                      : "h-9 min-w-0 border-border/60 bg-white text-foreground dark:bg-background/90"
                    }
                  />
                  {busy && (
                    <div className="flex max-w-[12rem] shrink-0 items-center rounded border border-border/60 bg-muted/70 px-2 py-1 text-xs text-muted-foreground sm:max-w-80">
                      <span className="truncate" title={spinnerText}>{spinnerText}</span>
                    </div>
                  )}

                  {gemmaThinkingAvailable && (
                    <div className="hidden shrink-0 items-center gap-2 rounded border border-border/60 bg-white/95 px-2 py-1 text-xs font-medium text-foreground shadow-sm dark:bg-muted/70 lg:flex">
                      <span>Gemma thinking</span>
                      <Switch
                        className={visibleSwitchClassName}
                        checked={gemmaThinkingEnabled}
                        onCheckedChange={(value) => {
                          setGemmaThinkingEnabled(value);
                          saveGemma4ThinkingEnabled(value);
                        }}
                      />
                    </div>
                  )}

                  <Button
                    disabled={busy || !input.trim()}
                    onClick={() => void handleCommand(input)}
                    className="h-9 shrink-0"
                  >
                    <Sparkles className="mr-1 h-4 w-4" />
                    {busy ? "Working…" : "Go"}
                  </Button>

                </div>

                {blockedMsg && (
                  <div className="hidden max-w-72 items-center gap-1 rounded bg-destructive/15 px-2 py-1 text-xs text-destructive lg:flex">
                    <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">AI blocked</span>
                  </div>
                )}

                {toolingError && (
                  <div className="hidden max-w-72 truncate rounded bg-amber-500/15 px-2 py-1 text-xs text-amber-700 dark:text-amber-300 xl:block">
                    Structured fallback active
                  </div>
                )}

                <div className="hidden shrink-0 items-center gap-2 md:flex">
                  <span className="text-xs text-taskbar-foreground/80">Jail mode</span>
                  <Switch checked={safeMode} onCheckedChange={setSafeMode} />
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        clearAutoHide();
                        setPinned((current) => {
                          const nextPinned = !current;
                          if (!nextPinned) {
                            setExpanded(false);
                          }
                          return nextPinned;
                        });
                      }}
                      aria-label={pinned ? "Unpin AI taskbar" : "Pin AI taskbar"}
                      title={pinned ? "Unpin" : "Pin"}
                      className="h-8 w-8 text-current hover:bg-[hsl(var(--taskbar-btn-hover))]"
                    >
                      {pinned ? <Pin className="h-4 w-4 text-current" /> : <PinOff className="h-4 w-4 text-current" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{pinned ? "Unpin AI taskbar" : "Pin AI taskbar"}</TooltipContent>
                </Tooltip>
              </div>
            </Card>
          </TooltipProvider>
        </div>
      </div>

      {answerBubble && (
        <div
          ref={answerBubbleRef}
          className="fixed z-[10020] w-[min(28rem,calc(100vw-2rem))] pointer-events-auto"
          style={bubblePosition ? { left: bubblePosition.x, top: bubblePosition.y } : { right: 16, top: 64 }}
        >
          <Card className="border border-border/80 bg-background/95 shadow-2xl backdrop-blur">
            <div className="flex items-start gap-3 p-3">
              <AITaskbarIcon className="mt-0.5 h-5 w-5 shrink-0 text-current" />
              <div className="min-w-0 flex-1">
                <div
                  className="flex items-start justify-between gap-2 cursor-move select-none"
                  onMouseDown={startBubbleDrag}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">Ask AI</div>
                    <div className="mt-1 break-words text-xs text-muted-foreground">{answerBubble.question}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => void handleCopyAnswerBubble()}
                      aria-label={answerCopied ? "Copied AI answer" : "Copy AI answer"}
                      title={answerCopied ? "Copied" : "Copy"}
                    >
                      {answerCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={() => {
                        resetAnswerCopiedState();
                        setPendingClarification(null);
                        setAnswerBubble(null);
                      }}
                      aria-label="Close Ask AI answer"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 max-h-72 overflow-auto rounded border bg-muted/40 p-3 text-sm text-foreground">
                  <div className="markdown-content prose prose-sm max-w-none break-words text-foreground dark:prose-invert [&_ol]:pl-5 [&_ul]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      }}
                    >
                      {cleanMarkdownBold(answerBubble.answer)}
                    </ReactMarkdown>
                  </div>
                </div>

                {pendingClarification && (
                  <div className="mt-3 space-y-2">
                    <div className="flex gap-2">
                      <Input
                        ref={bubbleReplyRef}
                        value={bubbleReply}
                        onChange={(event) => setBubbleReply(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            if (!bubbleReply.trim() || busy) return;
                            void handleCommand(bubbleReply);
                          }
                        }}
                        placeholder={pendingClarification.kind === "weather_location"
                          ? "Enter a city, region, or postal code"
                          : "Enter your city, province, region, or postal code"}
                        className="h-9 bg-background"
                      />
                      <Button
                        className="h-9 shrink-0"
                        disabled={!bubbleReply.trim() || busy}
                        onClick={() => void handleCommand(bubbleReply)}
                      >
                        {busy ? "Checking…" : "Reply"}
                      </Button>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      You can also type <span className="font-medium">cancel</span>.
                    </div>
                  </div>
                )}

                {answerBubble.actions && answerBubble.actions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {answerBubble.actions.map((action) => (
                      <Button
                        key={action.id}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 gap-2"
                        onClick={() => openPlaceInMapleGIS(action.place)}
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{action.appLabel}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

    </>
  );
};

export default DesktopAssistant;